import React, { useEffect, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import api from '../../services';

const Clients = () => {
    const initialFormData = {
        name: '',
        webhook_url: '',
        redirect_url: '',
        tolerance: 0,
        success_page_display: true
    };

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingClient, setEditingClient] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        fetchClients();
    }, []);

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const fetchClients = async () => {
        setLoading(true);
        const response = await api('clients', null, 'GET');
        if (response.status === 200) {
            setClients(response.data);
        }
        setLoading(false);
    };

    const handleEdit = (client) => {
        setEditingClient(client);
        setFormData({
            name: client.name,
            webhook_url: client.webhook_url,
            redirect_url: client.redirect_url,
            tolerance: client.tolerance,
            success_page_display: client.success_page_display
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this client?')) {
            const response = await api(`clients/${id}`, null, 'DELETE');
            if (response.status === 204) {
                showNotification('Client deleted successfully');
                fetchClients();
            } else {
                showNotification('Failed to delete client', 'error');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const method = editingClient ? 'PUT' : 'POST';
        const path = editingClient ? `clients/${editingClient.id}` : 'clients';

        const response = await api(path, formData, method);

        if (response.status === 200 || response.status === 201) {
            showNotification(`Client ${editingClient ? 'updated' : 'added'} successfully`);
            setShowForm(false);
            setEditingClient(null);
            setFormData(initialFormData);
            fetchClients();
        } else {
            showNotification(`Failed to ${editingClient ? 'update' : 'add'} client`, 'error');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2>Clients</h2>
                <button
                    onClick={() => { setShowForm(true); setEditingClient(null); setFormData(initialFormData); }}
                    style={{ padding: '8px 16px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                    Add Client
                </button>
            </div>

            {showForm && (
                <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                    <h3>{editingClient ? 'Edit Client' : 'New Client'}</h3>
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Webhook URL</label>
                            <input
                                type="url"
                                value={formData.webhook_url}
                                onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Redirect URL</label>
                            <input
                                type="url"
                                value={formData.redirect_url}
                                onChange={(e) => setFormData({...formData, redirect_url: e.target.value})}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Tolerance</label>
                            <input
                                type="number"
                                step="0.1"
                                value={formData.tolerance}
                                onChange={(e) => setFormData({...formData, tolerance: parseFloat(e.target.value)})}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                checked={formData.success_page_display}
                                onChange={(e) => setFormData({...formData, success_page_display: e.target.checked})}
                            />
                            <label>Show Success Page</label>
                        </div>
                        <div style={{ gridColumn: 'span 2', display: 'flex', gap: '12px', marginTop: '16px' }}>
                            <button type="submit" style={{ padding: '8px 24px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowForm(false); setEditingClient(null); setFormData(initialFormData); }}
                                style={{ padding: '8px 24px', background: 'white', border: '1px solid #d9d9d9', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#fafafa' }}>
                        <tr>
                            <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Name</th>
                            <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>API Key</th>
                            <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Tolerance</th>
                            <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map(client => (
                            <tr key={client.id}>
                                <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>{client.name}</td>
                                <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}><code>{client.api_key.substring(0, 5)}...</code></td>
                                <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>{client.tolerance}</td>
                                <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '12px' }}>
                                    <button onClick={() => handleEdit(client)} style={{ color: '#1890ff', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
                                    <button onClick={() => handleDelete(client.id)} style={{ color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer' }}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 24px' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Clients;
